import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { Camion, CamionCreate } from '../../types/weighing';

interface EditVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (vehicle: Partial<CamionCreate>) => void;
  vehicle: Camion;
  submitError?: string;
}

const EditVehicleModal: React.FC<EditVehicleModalProps> = ({ isOpen, onClose, onSave, vehicle, submitError: propSubmitError }) => {
  const [formData, setFormData] = useState<Partial<CamionCreate>>({
    immatriculation: '',
    type_vehicule: 'Vehicule',
    marque: '',
    modele: '',
    capacite: 0,
    proprietaire: '',
    nombre_passages: 0,
    tare_theorique: undefined
  });


  const [errors, setErrors] = useState<Partial<Record<keyof CamionCreate, string>>>({});
  const [submitError, setSubmitError] = useState<string>('');

  // Synchroniser avec l'erreur passée en prop
  useEffect(() => {
    if (propSubmitError) {
      setSubmitError(propSubmitError);
    }
  }, [propSubmitError]);


  useEffect(() => {
    if (vehicle) {
      setFormData({
        immatriculation: vehicle.immatriculation,
        type_vehicule: vehicle.type_vehicule || 'Vehicule',
        marque: vehicle.marque,
        modele: vehicle.modele,
        capacite: vehicle.capacite,
        proprietaire: vehicle.proprietaire,
        nombre_passages: vehicle.nombre_passages,
        tare_theorique: vehicle.tare_theorique
      });
      setSubmitError(''); // Effacer l'erreur lors du chargement des données
    }
  }, [vehicle]);

  const handleInputChange = (field: keyof CamionCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    // Effacer l'erreur de soumission quand l'utilisateur modifie le formulaire
    if (submitError) {
      setSubmitError('');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CamionCreate, string>> = {};

    // Validation de l'immatriculation selon le type de véhicule
    if (formData.type_vehicule === 'Vehicule') {
      if (!formData.immatriculation?.trim()) {
        newErrors.immatriculation = 'L\'immatriculation est requise pour les véhicules normaux';
      } else {
        const immatriculation = formData.immatriculation.trim();
        
        // Validation souple : accepter les immatriculations avec lettres et chiffres
        // Longueur minimale : 5 caractères, maximale : 15 caractères
        // Doit contenir au moins une lettre et un chiffre
        const hasLetter = /[A-Za-z]/.test(immatriculation);
        const hasDigit = /[0-9]/.test(immatriculation);
        const isValidLength = immatriculation.length >= 5 && immatriculation.length <= 15;
        // Accepter uniquement lettres, chiffres, tirets et espaces (mais pas que des espaces)
        const isValidFormat = /^[A-Za-z0-9\s-]+$/.test(immatriculation) && immatriculation.replace(/\s/g, '').length > 0;
        
        if (!isValidLength) {
          newErrors.immatriculation = 'L\'immatriculation doit contenir entre 5 et 15 caractères';
        } else if (!hasLetter || !hasDigit) {
          newErrors.immatriculation = 'L\'immatriculation doit contenir au moins une lettre et un chiffre';
        } else if (!isValidFormat) {
          newErrors.immatriculation = 'L\'immatriculation ne peut contenir que des lettres, chiffres, tirets et espaces';
        }
      }
    } else {
      // Pour les autres types, l'immatriculation est optionnelle mais si renseignée, elle doit être valide
      if (formData.immatriculation?.trim()) {
        // Validation plus souple pour les codes personnalisés (au moins 2 caractères)
        if (formData.immatriculation.trim().length < 2) {
          newErrors.immatriculation = 'Le code doit contenir au moins 2 caractères';
        }
      }
    }

    // Les champs modele, capacite et proprietaire ne sont plus obligatoires
    // Validation optionnelle pour la capacité si elle est renseignée
    if (formData.capacite !== undefined && formData.capacite < 0) {
      newErrors.capacite = 'La capacité ne peut pas être négative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Nettoyer les données avant l'envoi
      const cleanData = {
        ...formData,
        capacite: formData.capacite || 0,
        tare_theorique: formData.tare_theorique || undefined,
        nombre_passages: formData.nombre_passages || 0
      };
      
      onSave(cleanData);
      onClose();
    }
    // Si validation échoue, le modal reste ouvert et les erreurs sont affichées
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifier le véhicule" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Type de véhicule */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Type de véhicule <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-4">
            {['Vehicule', 'Fourchette', 'Tricycle', 'Vanette', 'Autre'].map((type) => (
              <label key={type} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="type_vehicule"
                  value={type}
                  checked={formData.type_vehicule === type}
                  onChange={(e) => handleInputChange('type_vehicule', e.target.value)}
                  className="text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">{type}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Immatriculation et Marque */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Immatriculation {formData.type_vehicule === 'Vehicule' && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={formData.immatriculation || ''}
              onChange={(e) => handleInputChange('immatriculation', e.target.value.toUpperCase())}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.immatriculation ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={formData.type_vehicule === 'Vehicule' ? "12AB345CD" : "Code personnalisé"}
            />
            {errors.immatriculation && (
              <p className="mt-1 text-sm text-red-600">{errors.immatriculation}</p>
            )}
            {formData.type_vehicule === 'Vehicule' && (
              <p className="mt-1 text-sm text-gray-500">Exemples : 5966CK07, AA876BC, AA966JY01, 12AB345CD, 12345CI01</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Marque/Type
            </label>
            <input
              type="text"
              value={formData.marque || ''}
              onChange={(e) => handleInputChange('marque', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.marque ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Renault, Mercedes, Volvo..."
            />
            {errors.marque && (
              <p className="mt-1 text-sm text-red-600">{errors.marque}</p>
            )}
          </div>
        </div>

        {/* Modèle et Capacité */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modèle
            </label>
            <input
              type="text"
              value={formData.modele || ''}
              onChange={(e) => handleInputChange('modele', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Master, Sprinter, Actros..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Capacité (kg)
            </label>
            <input
              type="number"
              value={formData.capacite || ''}
              onChange={(e) => handleInputChange('capacite', e.target.value ? parseFloat(e.target.value) : null)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.capacite ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="3500"
              min="0"
              step="0.01"
            />
            {errors.capacite && (
              <p className="mt-1 text-sm text-red-600">{errors.capacite}</p>
            )}
          </div>
        </div>

        {/* Propriétaire et Nombre de passages */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Propriétaire
            </label>
            <input
              type="text"
              value={formData.proprietaire || ''}
              onChange={(e) => handleInputChange('proprietaire', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Nom du propriétaire"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre de passages
            </label>
            <input
              type="number"
              value={formData.nombre_passages || 0}
              onChange={(e) => handleInputChange('nombre_passages', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="0"
              min="0"
            />
          </div>
        </div>

        {/* Tare théorique */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tare théorique (kg)
            </label>
            <input
              type="number"
              value={formData.tare_theorique || ''}
              onChange={(e) => handleInputChange('tare_theorique', e.target.value ? parseFloat(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="3500"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        {/* Message d'erreur de soumission */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Erreur lors de la modification du véhicule
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{submitError}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Mettre à jour
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditVehicleModal;
